import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { CourseFooterCta, CoursePlayerChrome } from "@/components/training/CoursePlayerChrome";
import { CompleteScreen } from "@/components/training/screens/CompleteScreen";
import { ContentScreen } from "@/components/training/screens/ContentScreen";
import { CoverScreen } from "@/components/training/screens/CoverScreen";
import { HowItWorksScreen } from "@/components/training/screens/HowItWorksScreen";
import { QuizScreen } from "@/components/training/screens/QuizScreen";
import { ScopeCheckScreen } from "@/components/training/screens/ScopeCheckScreen";
import { ScopeListScreen } from "@/components/training/screens/ScopeListScreen";
import { VideoScreen } from "@/components/training/screens/VideoScreen";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { courseTheme } from "@/lib/training/courseTheme";
import { clearScreenPosition, getScreenPosition, setScreenPosition } from "@/lib/training/progress";
import type { Course } from "@/lib/training/types";
import { useCoursePlayer } from "@/lib/training/useCoursePlayer";
import { completeTrainingCourse, getTrainingBundle } from "@/lib/training-api";

/**
 * The course player.
 *
 * Content comes from the API; which courses are finished is server state. Her
 * position INSIDE a course is kept on the device — the course is the save
 * point, so the most a lost device costs is one course.
 */
export default function CoursePlayerScreen() {
    const router = useRouter();
    const { bundleSlug, courseSlug } = useLocalSearchParams<{
        bundleSlug: string;
        courseSlug: string;
    }>();
    const { user } = useAuth();
    const rnId = user?.rnId ?? null;

    const [course, setCourse] = useState<Course | null>(null);
    const [startAt, setStartAt] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        if (!rnId || !courseSlug) {
            return;
        }
        void (async () => {
            try {
                const { bundle } = await getTrainingBundle(rnId, bundleSlug);
                const found = bundle.content.courses.find((c) => c.slug === courseSlug) ?? null;
                const resume = await getScreenPosition(bundleSlug, courseSlug);
                if (!alive) {
                    return;
                }
                setCourse(found);
                setStartAt(resume);
                if (!found) {
                    setError("That course isn't in your training.");
                }
            } catch (e: unknown) {
                if (alive) {
                    setError(e instanceof Error ? e.message : "Couldn't load the course");
                }
            }
        })();
        return () => {
            alive = false;
        };
    }, [rnId, courseSlug]);

    if (error) {
        return (
            <SafeAreaView edges={["top", "bottom"]} style={styles.centre}>
                <Text style={styles.error}>{error}</Text>
            </SafeAreaView>
        );
    }

    if (!course || startAt === null) {
        return (
            <SafeAreaView edges={["top", "bottom"]} style={styles.centre}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </SafeAreaView>
        );
    }

    return (
        <Player
            bundleSlug={bundleSlug}
            course={course}
            startAt={startAt}
            rnId={rnId}
            onExit={() => {
                // Land on this course's bundle, however the course was reached.
                //
                // Two failure modes this avoids. `replace` used to swap the
                // course for ANOTHER list entry, leaving [list, list] and one
                // more each time a course was opened and left — after three
                // courses, back had to be pressed four times. And `back()` is
                // wrong for a deep link, which opens the course directly on top
                // of the tabs: canGoBack() is true, but the screen underneath is
                // Home. dismissTo pops to the bundle when it is in the stack and
                // navigates there when it is not.
                router.dismissTo(`/training/${bundleSlug}`);
            }}
        />
    );
}

/**
 * Split out so `useCoursePlayer` mounts with the resume index already known —
 * the hook seeds its state once and must not be initialised with a placeholder.
 */
function Player({
    bundleSlug,
    course,
    startAt,
    rnId,
    onExit
}: {
    bundleSlug: string;
    course: Course;
    startAt: number;
    rnId: number | null;
    onExit: () => void;
}) {
    const player = useCoursePlayer(course, startAt);
    const [saving, setSaving] = useState(false);
    // A scopeCheck screen answers inline rather than page-by-page, so the footer
    // stays locked until every item on it has been answered.
    const [scope, setScope] = useState({ answered: 0, total: 0 });
    const onScopeProgress = useCallback(
        (answered: number, total: number) => setScope({ answered, total }),
        []
    );
    const finished = useRef(false);
    // Remember where she is, so closing the app mid-course resumes here.
    useEffect(() => {
        void setScreenPosition(bundleSlug, course.slug, player.idx);
    }, [player.idx, course.slug, bundleSlug]);

    async function finish() {
        if (!rnId || finished.current) {
            onExit();
            return;
        }
        finished.current = true;
        setSaving(true);
        try {
            await completeTrainingCourse(rnId, bundleSlug, course.slug);
            await clearScreenPosition(bundleSlug, course.slug);
        } catch {
            // The course is finished from her point of view and the server call
            // is idempotent, so a later retry reconciles. Don't trap her here.
            finished.current = false;
        } finally {
            setSaving(false);
            onExit();
        }
    }

    const s = player.screen;
    // A quiz gates the CTA until she gets it right; quizzes retry until correct.
    const quizLocked = s.kind === "quiz" && !player.quiz.solved;
    const scopeLocked = s.kind === "scopeCheck" && scope.answered < scope.total;

    return (
        <SafeAreaView edges={["top", "bottom"]} style={styles.page}>
            <CoursePlayerChrome
                modulePill={player.coursePill}
                progressPct={player.progressPct}
                timeLeftLabel={player.timeLeftLabel}
                stepLabel={player.stepLabel}
                // Back always does something: step to the previous screen when
                // there is one, otherwise leave the course. A single-screen
                // course (the scope refresher) has no previous screen, and an
                // inert arrow reads as broken.
                canBack
                onBack={player.canBack ? player.back : onExit}
                onClose={onExit}
            />

            <ScrollView contentContainerStyle={styles.body}>
                {s.kind === "cover" ? <CoverScreen screen={s} /> : null}
                {s.kind === "howItWorks" ? <HowItWorksScreen screen={s} /> : null}
                {s.kind === "video" ? <VideoScreen screen={s} /> : null}
                {s.kind === "content" ? <ContentScreen screen={s} /> : null}
                {s.kind === "quiz" ? (
                    <QuizScreen
                        screen={s}
                        quizIndex={player.quizIndex}
                        quizCount={player.quizCount}
                        selected={player.quiz.selected}
                        triedWrong={player.quiz.triedWrong}
                        solved={player.quiz.solved}
                        onSelect={player.selectOption}
                    />
                ) : null}
                {s.kind === "scopeCheck" ? (
                    <ScopeCheckScreen screen={s} onProgress={onScopeProgress} />
                ) : null}
                {s.kind === "scopeList" ? <ScopeListScreen screen={s} /> : null}
                {s.kind === "complete" ? <CompleteScreen screen={s} score={player.score} /> : null}
            </ScrollView>

            <CourseFooterCta
                label={
                    saving
                        ? "Saving…"
                        : s.kind === "quiz" && !player.quiz.solved
                          ? "Check answer"
                          : scopeLocked
                            ? `${scope.answered} of ${scope.total} answered`
                            : (s.cta ?? (player.isLastScreen ? "Finish course" : "Continue"))
                }
                showArrow={!player.isLastScreen && !quizLocked && !scopeLocked}
                disabled={saving || scopeLocked || (quizLocked && player.quiz.selected == null)}
                onPress={() => {
                    if (quizLocked) {
                        player.checkAnswer();
                        return;
                    }
                    if (player.isLastScreen) {
                        void finish();
                        return;
                    }
                    player.advance();
                }}
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    page: { flex: 1, backgroundColor: courseTheme.paper },
    centre: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: courseTheme.paper,
        padding: 24
    },
    error: { color: courseTheme.slate, textAlign: "center" },
    // Extra bottom room so the last card's controls clear the fixed footer CTA —
    // on a scopeCheck screen the Allowed / Not allowed buttons would otherwise
    // sit underneath it and swallow the tap.
    body: { padding: 20, paddingBottom: 96 }
});
